import React from 'react';
import { Alert, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import type { CallDto } from '@shared/services/apiService/apiService';
import { copyToClipboard } from '@pages/SuperAdmin/CompaniesPage/utils';
import { CallAnalysisJsonTree } from '@pages/Admin/Campaign/ui/SidebarTabs/components/CallAnalysisJsonTree';

interface AnalysisTabProps {
  analysisStatus: CallDto['analysisStatus'];
  analysis: CallDto['analysis'];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => Promise<unknown>;
}

const getStatusLabel = (status: CallDto['analysisStatus'], unavailable: string) => {
  if (!status) return unavailable;
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const AnalysisTab: React.FC<AnalysisTabProps> = ({
  analysisStatus,
  analysis,
  isLoading,
  isError,
  isFetching,
  onRetry,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
        <CircularProgress />
        <Typography>{t('campaignPage.transcript.loading')}</Typography>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack alignItems="flex-start" spacing={1.5} sx={{ py: 3 }}>
        <Alert severity="error">{t('common.errorMessage')}</Alert>
        <Button variant="contained" onClick={() => void onRetry()}>
          {t('common.retry')}
        </Button>
      </Stack>
    );
  }

  const isProcessing = analysisStatus === 'queued';
  const hasPayload = analysis !== null && analysis !== undefined;
  const prettyJson = hasPayload ? JSON.stringify(analysis, null, 2) : undefined;

  return (
    <Stack spacing={2} sx={{ py: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('campaignPage.technicalDetails.analysisStatus')}
          </Typography>
          <Chip
            size="small"
            label={getStatusLabel(analysisStatus, t('campaignPage.unavailable'))}
          />
        </Stack>
        {isProcessing ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={isFetching}
            onClick={() => void onRetry()}
            aria-label={t('campaignPage.operation.refresh')}>
            {t('campaignPage.operation.refresh')}
          </Button>
        ) : null}
      </Stack>

      {isProcessing ? <Alert severity="info">{t('campaignPage.analysisInQueue')}</Alert> : null}
      {analysisStatus === 'failed' ? (
        <Alert severity="error">{t('campaignPage.analysisFailed')}</Alert>
      ) : null}
      {!hasPayload ? (
        <Typography color="text.secondary">{t('campaignPage.analysisEmpty')}</Typography>
      ) : prettyJson === undefined ? (
        <Typography color="text.secondary">{t('campaignPage.analysisEmpty')}</Typography>
      ) : (
        <CallAnalysisJsonTree value={analysis} onCopyJson={() => copyToClipboard(prettyJson)} />
      )}
    </Stack>
  );
};
